import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Anchor, Box, Center, Flex, Stack, Text, Title } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import type { FC, ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';

import { Panel } from '../../components/global/Panel/Panel.tsx';
import { CenterLayout } from '../../components/layout/CenterLayout.tsx';
import { Button } from '../../components/ui/Button/Button.tsx';
import { SocialLoginButton } from '../../components/ui/SocialLoginButton/SocialLoginButton.tsx';
import { env } from '../../env.ts';
import { schema } from '../../forms/auth/SignUp/schema.ts';
import type { FormSignup } from '../../forms/auth/SignUp/schema.ts';
import { SignUp as SignUpFormContent } from '../../forms/auth/SignUp/SignUp.tsx';
import { useI18n } from '../../hooks/useI18n.ts';
import { signup } from '../../services/promoterAuthentication.ts';

const errorMessages = {
  VALIDATION_ERROR: <Text>Response is not valid</Text>,
  UNKNOWN_ERROR: <Text>Unknown error occurred</Text>,
  EMAIL_ALREADY_EXISTS: (
    <Text>
      Looks like this email is already registered. If you’re trying to log in, please head over to the{' '}
      <Link to="/auth/login">login page</Link>. Forgot your password? No worries, you can request a new one there!
    </Text>
  ),
  MAIL_SERVICE_UNAVAILABLE: <Text>We couldn&apos;t send a verification mail, please try again later</Text>,
} as const;

export const SignUp: FC = () => {
  const { content } = useI18n();
  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState<ReactNode | undefined>(undefined);

  const { mutate, isPending } = useMutation({
    mutationFn: async (params: FormSignup) => signup(params),
    onSuccess: (result) => {
      setErrorMessage(undefined);

      if ('success' in result && result.success) {
        // Only the email, for "resend code". Verifying the code logs the promoter in.
        localStorage.setItem('tbrem', result.email);
        void navigate(`/auth/verification-email-sent/${result.id}`);
        return;
      }

      if ('type' in result) {
        setErrorMessage(errorMessages[result.type]);
      }
    },
  });

  const formMethods = useForm<FormSignup>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  if (!content?.signup) {
    return null;
  }

  const {
    title,
    intro_text,
    facebook_button_label,
    google_button_label,
    inbetween_text,
    go_to_login_description,
    go_to_login_text,
    image,
    cta_button,
  } = content.signup;

  return (
    <CenterLayout imageSrc={image ? `${env.VITE_DIRECTUS_API_URL}/assets/${image}` : undefined}>
      <Panel>
        <Title order={1}>{title}</Title>
        <Text size="xs">{intro_text}</Text>
        <Stack
          component="form"
          gap="sm"
          onSubmit={(event) => void formMethods.handleSubmit((data) => mutate(data))(event)}
        >
          <SignUpFormContent useFormValue={formMethods} />
          <Button loading={isPending} text={cta_button ?? 'Sign up'} type="submit" />
        </Stack>
        {!!errorMessage && (
          <Alert color="gigi-apple-pink" p="xs" radius="xs">
            {errorMessage}
          </Alert>
        )}
        <Flex align="center" gap="xxs">
          <Box bg="gigi-outlines" flex="1 0 auto" h={2} />
          <Text size="sm" ta="center">
            {inbetween_text}
          </Text>
          <Box bg="gigi-outlines" flex="1 0 auto" h={2} />
        </Flex>
        <Stack gap="xxs">
          <SocialLoginButton text={facebook_button_label ?? 'Signup with facebook'} type="facebook" />
          <SocialLoginButton text={google_button_label ?? 'Signup with google'} type="google" />
        </Stack>
        <Center>
          <Text c="gigi-grey-text" mt="xl" size="xs" ta="center">
            {go_to_login_description}{' '}
            <Anchor component={Link} to="/auth/login">
              {go_to_login_text}
            </Anchor>
          </Text>
        </Center>
      </Panel>
    </CenterLayout>
  );
};
